// Shapes and presentation helpers for the Pipeline Stages page.
//
// Nothing here decides which stage an application is in, or why it was
// rejected. That is driver_pipeline_rows_v1 in Postgres (migration 0277), and
// the tiles and the lists both read it. This file only turns keys into words
// and routes, so it stays a plain .ts module the node test runner can import.

export type StageSplit = { key: string; n: number }

export type StageTile = {
  key: string
  label: string
  tracked: boolean
  counted_as: string
  // null when the stage is not tracked: a figure that does not exist is shown
  // as missing, never as zero.
  n: number | null
  // How many of n are counted in the page total. Training and Induction pending
  // also show active drivers with a form outstanding; those are counted under
  // Active and arrive here as from_active, so adding in_total never counts a
  // person twice.
  in_total: number
  from_active: number
  recent: number | null
  older: number | null
  // Applications in this tile whose reminders ran out with no answer (0279).
  no_response: number
  splits: StageSplit[]
}

export type ReminderSummary = {
  enabled: boolean
  open: number
  sent_last_24h: number
  no_response: number
  declined: number
}

export type ReasonCount = { code: string; label: string; derived: boolean; n: number }

export type StageCounts = {
  allowed: boolean
  message?: string
  as_of?: string
  total?: number
  applications_excluding_tests?: number
  stages?: StageTile[]
  rejected_by_stage?: StageSplit[]
  application_reasons?: ReasonCount[]
  months?: { month: string; n: number }[]
  reminders?: ReminderSummary
  can_block?: boolean
  can_lift?: boolean
}

export type StageRow = {
  application_id: string
  person_id: string
  full_name: string | null
  primary_email: string | null
  primary_phone: string | null
  current_status: string
  stage: string
  reject_stage: string | null
  block_kind: string | null
  block_source: string | null
  block_id: string | null
  block_reason: string | null
  reason_code: string | null
  reason_label: string | null
  reason_derived: boolean | null
  status_note: string | null
  score: number | null
  score_rubric_version: string | null
  // From the submitted scorecards, e.g. "18 of 30" and "Average 72.5%". Null
  // when nobody scored the interview; interviews.result is an outcome code and
  // is deliberately not sent.
  i1_score: string | null
  i2_score: string | null
  scored_count: number
  scorer_seats: number
  training_outstanding: boolean
  induction_outstanding: boolean
  reminders_sent: number
  reminder_state: 'reminding' | 'no_response' | null
  applied_at: string | null
  applied_month: string | null
  recent: boolean
  in_stage_since: string | null
  days_in_stage: number | null
  manager_name: string | null
}

export type StageList = {
  allowed: boolean
  message?: string
  stage?: string
  rows?: StageRow[]
  months?: { month: string; n: number }[]
}

export type StageFilter = {
  month?: string
  status?: string
  reject_stage?: string
  reason?: string
  block?: string
  reminder?: string
}

export const BLOCK_KIND_LABELS: Record<string, string> = {
  no_show: 'No show',
  declined: 'Not interested: said no thanks',
  no_response: 'Not interested: no response',
  do_not_interview: 'Do not interview again',
  dismissal: 'Dismissal',
  flagged: 'Flagged at application',
}

export function blockKindLabel(key: string | null | undefined): string {
  if (!key) return 'Not recorded'
  return BLOCK_KIND_LABELS[key] ?? key
}

// The block kinds an admin may lift from this page. A dismissal is an HR
// decision and is not lifted here; pipeline_block_lift_v1 refuses it too.
export function liftable(kind: string | null | undefined): boolean {
  return kind === 'no_show' || kind === 'declined' || kind === 'no_response' || kind === 'do_not_interview'
}

// Split keys are application statuses, except the one for active drivers shown
// in the Training and Induction tiles.
export function splitLabel(key: string, statusLabel: (s: string) => string): string {
  if (key === 'active_driver') return 'Active drivers, form not signed'
  return statusLabel(key)
}

export function reminderLabel(row: Pick<StageRow, 'reminder_state' | 'reminders_sent'>): string | null {
  if (row.reminder_state === 'no_response') return 'No response after 7 reminders'
  if (row.reminder_state === 'reminding') return `Reminder ${row.reminders_sent} of 7 sent`
  return null
}

// Statuses at which a manager may still stop someone being interviewed. Past
// the interviews the button would read as dismissing an employee, which it
// does not do; the block still applies, but it is not offered there.
export function canStopInterviewing(status: string): boolean {
  return ['submitted', 'interview_1_pending', 'interview_1_booked', 'interview_2_pending', 'interview_2_booked',
    'rejected_score', 'interview_1_failed', 'interview_2_failed', 'withdrawn'].includes(status)
}

export const REJECT_STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  interview_1: 'Interview 1',
  interview_1_no_show: 'Interview 1 no show',
  interview_2: 'Interview 2',
  withdrew: 'Withdrew',
  dismissed: 'Dismissed',
  resigned: 'Resigned',
}

export function rejectStageLabel(key: string | null | undefined): string {
  if (!key) return 'Not recorded'
  return REJECT_STAGE_LABELS[key] ?? key
}

// Categories exist only to separate groups of tiles by colour. The brand
// crimson carries the figures; these carry the grouping.
export type StageCategory = 'closed' | 'interview' | 'hiring' | 'working' | 'other'

export function stageCategory(key: string): StageCategory {
  if (key === 'rejected' || key === 'blocked') return 'closed'
  if (key.startsWith('interview_')) return 'interview'
  if (['offer_pending', 'onboarding_pending', 'contract_pending', 'training_pending', 'induction_pending'].includes(key))
    return 'hiring'
  if (key === 'active' || key === 'suspended') return 'working'
  return 'other'
}

export const CATEGORY_LABELS: Record<StageCategory, string> = {
  closed: 'Closed',
  interview: 'Interviews',
  hiring: 'Hiring',
  working: 'Working',
  other: 'Also counted',
}

// Where an application opens in the rest of the cockpit. The Driver Pipeline
// page has four tabs and Active Drivers holds the employed; a link that opens
// the wrong tab opens to nobody, so every status is placed.
export function applicationRoute(status: string, applicationId: string): string {
  const id = encodeURIComponent(applicationId)
  if (['active', 'offboarding', 'compliance', 'suspended', 'dismissed', 'resigned'].includes(status)) return `/active?app=${id}`
  if (['rejected_score', 'interview_1_failed', 'interview_2_failed', 'interview_1_no_show', 'withdrawn'].includes(status))
    return `/pipeline?tab=rejected&app=${id}`
  if (status.startsWith('interview') || status === 'submitted') return `/pipeline?tab=interview&app=${id}`
  return `/pipeline?tab=offer&app=${id}`
}

export function day(value: string | null | undefined): string {
  if (!value) return 'Not recorded'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value)
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export function monthLabel(ym: string | null | undefined): string {
  if (!ym) return 'Not recorded'
  const m = /^(\d{4})-(\d{2})$/.exec(ym)
  if (!m) return ym
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

export function figure(n: number | null | undefined): string {
  if (n == null) return 'Not tracked'
  return n.toLocaleString('en-US')
}

// The scored progress on an Interview 2, as the brief asks for it: "1 of 2".
export function scoredLabel(row: Pick<StageRow, 'scored_count' | 'scorer_seats'>): string {
  return `${row.scored_count} of ${row.scorer_seats} scored`
}

// The page total: every application once. Uses in_total, not n, because the
// Training and Induction tiles also show active drivers counted under Active.
export function sumTiles(stages: StageTile[]): number {
  return stages.reduce((s, t) => s + (t.in_total ?? 0), 0)
}

export function matchesSearch(row: StageRow, term: string): boolean {
  const t = term.trim().toLowerCase()
  if (!t) return true
  return [row.full_name, row.primary_email, row.primary_phone, row.manager_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(t)
}

// ---------------------------------------------------------------------------
// The two funnels. Pure shaping of the counts the database already returned:
// which tile goes on which funnel, in what order, and what a click opens.
// ---------------------------------------------------------------------------

export type FunnelRow = {
  id: string
  label: string
  n: number
  recent: number | null
  // What a click opens: a stage, and optionally the filter within it, as the
  // URL parameters the page already reads.
  stage: string
  params: Record<string, string>
  level: 0 | 1
}

// Top to bottom, the order an applicant moves through. Training pending has no
// bar of its own here: every person in it is an active driver, shown under
// Active.
export const LIVE_ORDER = [
  'interview_1_pending',
  'interview_1_booked',
  'interview_2_pending',
  'interview_2_booked',
  'interview_2_scored',
  'offer_pending',
  'onboarding_pending',
  'contract_pending',
  'induction_pending',
  'active',
] as const

// Counted in the total but not a step anyone moves through. Being read is an
// application the document reader has not finished with, usually for
// minutes; drawn on top it put a one-person bar above the widest one.
export const LIVE_ASIDE = ['submitted', 'suspended', 'compliance'] as const

export function liveFunnel(tiles: StageTile[]): FunnelRow[] {
  const by = new Map(tiles.map((t) => [t.key, t]))
  const rows: FunnelRow[] = []
  for (const key of LIVE_ORDER) {
    const t = by.get(key)
    if (!t) continue
    // A bar counts the people AT the stage. Induction pending also shows active
    // drivers with the form outstanding; they get their own bar under Active.
    const own = t.from_active > 0
    rows.push({
      id: key,
      label: t.label,
      n: own ? t.in_total : (t.n ?? 0),
      recent: own ? null : t.recent,
      stage: key,
      params: own ? { status: 'own' } : {},
      level: 0,
    })
    if (key === 'active') {
      for (const [sub, label] of [
        ['training_pending', 'Training form not signed'],
        ['induction_pending', 'Induction form not signed'],
      ] as const) {
        const s = by.get(sub)
        if (s && s.from_active > 0) {
          rows.push({ id: `${sub}:active`, label, n: s.from_active, recent: null, stage: sub, params: { status: 'active_driver' }, level: 1 })
        }
      }
    }
  }
  return rows
}

export function closedFunnel(counts: StageCounts): FunnelRow[] {
  const by = new Map((counts.stages ?? []).map((t) => [t.key, t]))
  const rows: FunnelRow[] = []
  const rejected = by.get('rejected')
  if (rejected) {
    rows.push({ id: 'rejected', label: 'Rejected', n: rejected.n ?? 0, recent: rejected.recent, stage: 'rejected', params: {}, level: 0 })
    for (const s of counts.rejected_by_stage ?? []) {
      rows.push({ id: `rejected:${s.key}`, label: rejectStageLabel(s.key), n: s.n, recent: null, stage: 'rejected', params: { reject: s.key }, level: 1 })
    }
  }
  const blocked = by.get('blocked')
  if (blocked) {
    rows.push({ id: 'blocked', label: 'Blocked', n: blocked.n ?? 0, recent: blocked.recent, stage: 'blocked', params: {}, level: 0 })
    for (const s of blocked.splits) {
      rows.push({ id: `blocked:${s.key}`, label: blockKindLabel(s.key), n: s.n, recent: null, stage: 'blocked', params: { block: s.key }, level: 1 })
    }
  }
  return rows
}

// Bar width as a percentage of the funnel's widest bar, on a square root
// scale. Linear would draw Onboarding pending (3) at a fifth of a percent of
// Interview 1 pending (1,669): invisible. The printed number is the figure;
// the width only has to rank. Zero draws nothing, never a sliver that reads
// as some.
export function funnelWidth(n: number, max: number, min = 6): number {
  if (n <= 0 || max <= 0) return 0
  return Math.max(min, Math.round(Math.sqrt(n / max) * 1000) / 10)
}
